import React, { useEffect, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
// import Toast from '../../toast';
import { ArrowRight, ArrowLeft, SquareCheck } from 'lucide-react';

function ChooseManyQuestion({ response, question, handleUpdate, handleDelete, currentQuestionIndex, handleNextQuestion, isLastQuestion, isButtonsDisabled, submitAssessment }) {
  // choose top 3 choices then choose bottom 1 choice
  const { id, text, options, displayIndex } = question;

  const [selectedOptions, setSelectedOptions] = useState(response?.best || []);
  const [selectedLeastOption, setSelectedLeastOption] = useState(response?.least || null);
  const [showFirstQuestionPart, setShowFirstQuestionPart] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [showFirstQuestionPart]);

  const handleBestOptions = (option) => {
    if (selectedOptions.includes(option)) {
      setSelectedOptions(selectedOptions.filter((selectedOption) => selectedOption !== option));
    } else if (selectedOptions.length < 3) {
      setSelectedOptions([...selectedOptions, option]);
    }

    // show toast if user tries to select more than 3 options
    if (selectedOptions.length === 3 && !selectedOptions.includes(option)) {
      setShowToast(true);
    }
  };

  const handleLeastOption = (option) => {
    if (selectedLeastOption === option) {
      setSelectedLeastOption(null);
    } else if (!selectedLeastOption) {
      setSelectedLeastOption(option);
    }
    // show toast if user tries to select more than 1 worst option
    if (selectedLeastOption && selectedLeastOption !== option) {
      setShowToast(true);
    }
  };

  // one write for the whole answer. Writing `best` on the way into part two left
  // documents holding a `best` with no `least` - or, when the athlete arrived on
  // part two of a question they had never seen part one of, a `least` with no
  // `best` - and scoring counts either shape as unanswered.
  const submitResponse = async () => {
    setIsSaving(true);

    try {
      await handleUpdate(id, { best: selectedOptions, least: selectedLeastOption });
    } catch (err) {
      // the write hook records its own failure, and the server refuses a gapped
      // submission - keep the athlete moving rather than trapping them here
      console.log('Unable to save choose-many response', err);
    } finally {
      setIsSaving(false);
    }

    if (isLastQuestion) {
      submitAssessment();
    } else {
      handleNextQuestion(currentQuestionIndex + 1);
    }
  };

  const sortedOptions = options.sort((a, b) => a.id - b.id);

  // render the first part of the question (choose top 3 options)
  // then render the second part of the question (choose bottom 1 option) with the top 3 options disabled
  // show the best selected options in a 1, 2, 3 order

  const renderButtons = () => {
    return (
      <div className="button-container choose-many">
        <button
          className="button grey-outline move-button"
          disabled={isButtonsDisabled || isSaving}
          onClick={() => {
            if (showFirstQuestionPart) {
              // leaving for the previous question - it renders its own part one.
              // Forcing part two here is what stranded athletes on "pick one LEAST
              // like you" for a question whose top three they had never chosen.
              handleNextQuestion(currentQuestionIndex - 1);
            } else {
              setShowFirstQuestionPart(true);
            }
          }}>
          <ArrowLeft />
        </button>
        <div className="divider"></div>
        <button
          disabled={isButtonsDisabled || isSaving || (showFirstQuestionPart ? selectedOptions.length < 3 : !selectedLeastOption)}
          className="button primary-button move-button"
          onClick={() => {
            if (showFirstQuestionPart) {
              setShowFirstQuestionPart(false);
            } else {
              submitResponse();
            }
          }}>
          {isLastQuestion && !showFirstQuestionPart ? 'Submit' : <ArrowRight />}
        </button>
      </div>
    );
  };

  return (
    <div className="assessment-question-container">
      <div className="question-container">
        <div className="question-number">{displayIndex}.</div>
        <div className="question-text" dangerouslySetInnerHTML={{ __html: showFirstQuestionPart ? text : 'From the list below, please pick one statement that you feel is LEAST like you.' }}></div>
      </div>
      {showFirstQuestionPart && (
        <div className="question-options-container choose-many">
          {sortedOptions.map((option, index) => (
            <button
              key={index}
              className={`button question-button choose-many best-three-options ${selectedOptions.includes(option.id) ? 'selected' : ''}`}
              onClick={(e) => {
                handleBestOptions(option.id);
              }}>
              <div>{option.label}</div>
              {selectedOptions.includes(option.id) && (
                <div className="check-mark">
                  <SquareCheck size={18} />
                </div>
              )}
            </button>
          ))}
          {renderButtons()}
        </div>
      )}
      {!showFirstQuestionPart && (
        <div className="question-options-container choose-many">
          {sortedOptions
            .filter((option) => !selectedOptions.includes(option.id))
            .map((option, index) => (
              <button
                key={index}
                disabled={selectedOptions.includes(option.label)}
                className={`button question-button choose-many least-option ${selectedLeastOption === option.id ? 'selected' : ''}`}
                onClick={(e) => {
                  handleLeastOption(option.id);
                }}>
                {option.label}
              </button>
            ))}
          {renderButtons()}
        </div>
      )}
      {/* <CSSTransition in={showToast} timeout={2000} classNames="toast" unmountOnExit> */}
      {/* {showToast && <Toast text="Deselect an option before selecting a different choice" type="info" onDismiss={setShowToast} />} </CSSTransition> */}
    </div>
  );
}

export default ChooseManyQuestion;
